import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * APG modal dialog behaviour: moves focus inside on open (initialFocus or first focusable), keeps Tab inside,
 * closes on Escape, locks page scroll, and returns focus to the opener on close.
 */
export function useModal(ref: RefObject<HTMLElement | null>, onClose: () => void, initialFocus?: RefObject<HTMLElement | null>) {
  const close = useRef(onClose);
  useEffect(() => { close.current = onClose; }, [onClose]);
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first = initialFocus?.current ?? node?.querySelector<HTMLElement>(FOCUSABLE) ?? node;
    first?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); close.current(); return; }
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!items.length) { e.preventDefault(); return; }
      const a = items[0]!, z = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [ref, initialFocus]);
}

/** Centered modal dialog. Render conditionally: {open ? <Dialog .../> : null}. */
export function Dialog({ title, description, onClose, children, actions, initialFocus }: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  actions?: ReactNode;
  initialFocus?: RefObject<HTMLElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const tid = useId(), did = useId();
  useModal(ref, onClose, initialFocus);
  return createPortal(
    <>
      <div className="dialog-backdrop" onClick={onClose} />
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={tid} aria-describedby={description ? did : undefined} ref={ref} tabIndex={-1}>
        <div className="dialog-head">
          <h2 id={tid} className="h3">{title}</h2>
          <button type="button" className="btn quiet small" onClick={onClose}>Close</button>
        </div>
        {description ? <p id={did} className="secondary">{description}</p> : null}
        {children}
        {actions ? <div className="dialog-actions">{actions}</div> : null}
      </div>
    </>,
    document.body,
  );
}

/** Bottom sheet on phones; with side, a right-hand panel from 900px up. Same modal behaviour as Dialog. */
export function Sheet({ title, onClose, children, side }: { title: string; onClose: () => void; children: ReactNode; side?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const tid = useId();
  useModal(ref, onClose);
  return createPortal(
    <>
      <div className="sheet-backdrop" onClick={onClose} />
      <div className={`sheet${side ? " side" : ""}`} role="dialog" aria-modal="true" aria-labelledby={tid} ref={ref} tabIndex={-1}>
        <div className="sheet-head">
          <h2 id={tid} className="h3">{title}</h2>
          <button type="button" className="btn ghost small" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </>,
    document.body,
  );
}
